import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { Breathing } from "@/components/landing/Breathing";
import { WHATSAPP_URL, BOOK_PERIO_PATH } from "@/components/landing/data";
import { FEAR_STEPS, FEAR_FAQ } from "@/content/fear";

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FEAR_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

const Ctas = ({ dark = false }) => (
  <div className="mt-10 flex flex-wrap gap-3">
    <Link to={BOOK_PERIO_PATH} data-testid={`fear-cta-booking${dark ? "-2" : ""}`}
      className={`inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm transition-colors ${dark ? "bg-[#86C89B] text-[#0F2317] hover:bg-[#FCFBF7]" : "bg-[#1B3B28] text-[#FCFBF7] hover:bg-[#3E6B48]"}`}>
      Prenota un primo incontro <ArrowUpRight size={15} />
    </Link>
    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid={`fear-cta-whatsapp${dark ? "-2" : ""}`}
      className={`inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-sm transition-colors ${dark ? "border-[#FCFBF7]/25 text-[#FCFBF7] hover:bg-[#FCFBF7]/10" : "border-[#1B3B28]/20 text-[#1B3B28] hover:bg-[#E6ECE4]"}`}>
      <MessageCircle size={15} /> Scrivici, anche solo per parlare
    </a>
  </div>
);

const Step = ({ s, i }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, delay: (i % 3) * 0.08 }} data-testid={`fear-step-${s.n}`}
    className="rounded-3xl border border-[#FCFBF7]/10 p-7 sm:p-8 bg-[#FCFBF7]/[0.03]">
    <span className="f-mono text-xs tracking-[0.2em] text-[#86C89B]">{s.n}</span>
    <h3 className="f-serif text-3xl text-[#FCFBF7] mt-8 leading-tight">{s.title}</h3>
    <p className="text-[#FCFBF7]/70 mt-3 leading-relaxed">{s.text}</p>
  </motion.div>
);

const Hero = () => (
  <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-36 sm:pt-44 pb-24 grid lg:grid-cols-12 gap-12 items-center">
    <div className="lg:col-span-7">
      <p className="eyebrow text-[#3E6B48]">Per chi ha paura del dentista</p>
      <h1 className="f-serif text-5xl sm:text-7xl text-[#1B3B28] mt-5 leading-[0.98]" data-testid="fear-title">
        <MaskLine>Hai paura del dentista?</MaskLine>
        <MaskLine delay={0.12}><em className="text-[#3E6B48]">Sei nel posto giusto.</em></MaskLine>
      </h1>
      <Reveal delay={0.3}>
        <p className="text-lg text-[#526356] mt-8 max-w-xl leading-relaxed">
          Tante persone rimandano per anni le cure proprio per timore, spesso dopo una brutta esperienza. Non è debolezza
          e non devi vergognartene. Il nostro modo di lavorare è pensato, fin dal primo minuto, per chi è in ansia.
        </p>
        <Ctas />
      </Reveal>
    </div>
    <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.2}>
      <div className="rounded-[2rem] bg-[#0F2317] p-7"><Breathing /></div>
    </Reveal>
  </section>
);

export default function FearPage() {
  useSeo({
    title: "Paura del dentista? Il nostro metodo per pazienti ansiosi",
    description: "Hai paura del dentista? Al Centro Dentistico Galliera il Dott. Nicola Mauro accoglie i pazienti ansiosi con un metodo in 6 passi: conversazione, segnale di stop, laser delicato, pause e, su indicazione, sedazione cosciente.",
    path: "/paura-del-dentista",
    jsonLd: JSONLD,
  });
  return (
    <BlogLayout>
      <div data-testid="fear-page">
        <Hero />
        <section className="bg-[#0F2317] py-24 sm:py-32" data-testid="fear-steps-section">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
            <p className="eyebrow text-[#86C89B]">Il nostro metodo</p>
            <h2 className="f-serif text-4xl sm:text-5xl text-[#FCFBF7] leading-[1.02] mt-5 max-w-2xl">Sei passi, <em className="text-[#86C89B]">tutti al tuo ritmo.</em></h2>
            <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">{FEAR_STEPS.map((s, i) => <Step key={s.n} s={s} i={i} />)}</div>
          </div>
        </section>
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32 grid lg:grid-cols-12 gap-12" data-testid="fear-faq-section">
          <div className="lg:col-span-4">
            <p className="eyebrow text-[#3E6B48]">Domande che ci fanno spesso</p>
            <h2 className="f-serif text-4xl sm:text-5xl text-[#1B3B28] leading-[1.02] mt-5">Chiedere è <em className="text-[#3E6B48]">già un passo.</em></h2>
          </div>
          <Accordion type="single" collapsible className="lg:col-span-7 lg:col-start-6 border-t border-[#1B3B28]/15" data-testid="fear-faq">
            {FEAR_FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={`f-${i}`} className="border-b border-[#1B3B28]/15">
                <AccordionTrigger data-testid={`fear-faq-trigger-${i}`} className="f-serif text-2xl text-left text-[#1B3B28] py-6 hover:no-underline hover:text-[#3E6B48]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-[#526356] leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
        <section className="bg-[#1B3B28] py-24" data-testid="fear-cta">
          <div className="max-w-4xl mx-auto px-6 sm:px-10">
            <h2 className="f-serif text-4xl sm:text-5xl text-[#FCFBF7] leading-[1.05]">Il primo passo può essere <em className="text-[#86C89B]">solo una chiacchierata.</em></h2>
            <p className="text-[#FCFBF7]/75 mt-5 leading-relaxed">Prenota senza impegno: ti richiamiamo noi, con calma. Oppure prova prima il <Link to="/test-gengive" className="underline underline-offset-4" data-testid="fear-quiz-link">test delle gengive</Link>.</p>
            <Ctas dark />
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
